
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/image";

type CourseCardProps = {
  id: string;
  title: string;
  description?: string;
  level: string;
  price: string;
  status: string;
  image_url: string | null;
  enrolled?: boolean;
}

export function OptimizedCourseCard({
  id,
  title,
  level,
  price,
  status,
  image_url,
  enrolled,
}: CourseCardProps) {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm flex flex-col h-full">
      <div className="h-48 bg-gray-100">
        <OptimizedImage 
          src={image_url || "/placeholder.svg"} 
          alt={`Course: ${title}`}
          title={`Course: ${title} - ${level} Level - ${price}`}
          className="w-full h-full object-cover" 
          width={384}
          height={192}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.onerror = null;
            target.src = "/placeholder.svg";
          }}
        />
      </div>
      <div className="p-6 flex-grow">
        <div className="flex justify-between items-center mb-2">
          <div className="flex gap-2">
            <Badge variant={status === "Enrolling" ? "default" : "secondary"}>
              {status}
            </Badge>
            {enrolled && (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Enrolled
              </Badge>
            )}
          </div>
          <span className="font-bold text-lg">{price}</span>
        </div>
        <h3 className="text-xl font-bold mb-4">{title}</h3>
        <p className="text-sm text-gray-500">
          <span className="font-medium">Level:</span> {level}
        </p>
      </div>
      <div className="px-6 pb-6">
        {enrolled ? (
          <Link to={`/courses/${id}/lessons`} className="w-full">
            <Button className="w-full" aria-label={`Continue learning ${title}`}>Continue Learning</Button>
          </Link>
        ) : (
          <Link to={`/courses/${id}`} className="w-full">
            <Button 
              className="w-full" 
              variant="default"
              aria-label={status === "Enrolling" ? `Enroll now in ${title}` : `Get notified about ${title}`}
            >
              {status === "Enrolling" ? "Enroll Now" : "Get Notified"}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
